import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import slugify from "slugify";

import { Category, Equipment } from "../../types";
import Api from "../../Api";
import EntitySearch from "../../parts/EntitySearch";
import CategoryList from "./parts/CategoryList";
import Section from "../../parts/Section";
import BoolTabs from "../../parts/BoolTabs";
import Fab from "../../parts/Fab";
import DiscreetMessage from "../../parts/DiscreetMessage";
import Money from "../../parts/Money";
import EquipmentControls from "./parts/EquipmentControls";

const EquipmentsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [data, setData] = useState<Equipment[] | null>(null);

  const showCategories = searchParams.get("tab") === "categorias";
  const blocked = searchParams.get("bloqueados") === "true";
  const category = searchParams.get("categoria") ?? null;

  const load = useCallback(() => {
    Api.equipmentsListCategories().then(setCategories);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const searchFunction = useCallback(
    (searchTerm: string, page: string) => {
      return Api.equipmentsList(
        blocked.toString(),
        category
          ? categories!.find(
              (c) => category === slugify(c.name, { lower: true })
            )!.uuid
          : "",
        searchTerm,
        page
      );
    },
    [blocked, category, categories]
  );

  const selectCategory = (category: Category) => {
    setData(null);
    setSearchParams((params) => {
      params.set("categoria", slugify(category.name, { lower: true }));
      params.delete("tab");
      return params;
    });
  };

  const selectBlocked = (blocked: boolean) => {
    setData(null);
    setSearchParams((params) => {
      blocked ? params.set("bloqueados", "true") : params.delete("bloqueados");
      return params;
    });
  };

  if (!categories) return <DiscreetMessage text="Carregando ..." />;

  return (
    <>
      <div className="border-b">
        <Section className="p-4">
          <EntitySearch
            searchFunction={searchFunction}
            setEntities={setData}
            onSearch={() => {
              setSearchParams((params) => {
                params.delete("tab");
                return params;
              });
            }}
          />
        </Section>
      </div>

      <BoolTabs
        defaultTab={{ label: "Equipamentos", value: "equipamentos" }}
        alternateTab={{ label: "Categorias", value: "categorias" }}
        onChange={() => {}}
      />

      {!showCategories && (
        <EquipmentControls
          blocked={blocked}
          category={
            category
              ? categories.find(
                  (c) => category === slugify(c.name, { lower: true })
                )!
              : null
          }
          onChangeBlocked={(blocked) => () => selectBlocked(blocked)}
          onClearCategory={() =>
            setSearchParams((params) => {
              params.delete("categoria");
              return params;
            })
          }
        />
      )}

      <Section className="p-4 flex flex-col gap-4 pb-32">
        {showCategories && (
          <CategoryList
            categories={categories!}
            onSelect={selectCategory}
            onCreate={load}
          />
        )}

        {!showCategories && !data && <DiscreetMessage text="Carregando ..." />}

        {!showCategories &&
          data &&
          data.map((equipment) => (
            <EquipmentCard equipment={equipment} key={equipment.uuid} />
          ))}

        {!showCategories && data && data.length === 0 && (
          <DiscreetMessage text="Nenhum resultado" />
        )}

        {!showCategories && <Fab href="/equipamentos/criar" />}
      </Section>
    </>
  );
};

export default EquipmentsList;

const EquipmentCard = ({ equipment }: { equipment: Equipment }) => (
  <Link
    className="border rounded-lg shadow-sm p-4 flex flex-col gap-3 hover:bg-slate-50"
    to={`/equipamentos/${equipment.uuid}`}
  >
    <div className="overflow-scroll whitespace-nowrap no-scrollbar font-semibold">
      {equipment.name}
    </div>

    <div className="flex gap-2 flex-wrap">
      <Badge className={equipment.canSell ? "text-green-700" : "text-red-700"}>
        <FontAwesomeIcon icon={equipment.canSell ? "check" : "xmark"} />
        <span>Venda</span>
        {equipment.canSell && (
          <Money className="font-bold" cents={equipment.price} />
        )}
      </Badge>

      <Badge className={equipment.canRent ? "text-green-700" : "text-red-700"}>
        <FontAwesomeIcon icon={equipment.canRent ? "check" : "xmark"} />
        <span>Locação</span>
        {equipment.canRent && (
          <Money className="font-bold" cents={equipment.rent} />
        )}
      </Badge>
    </div>
  </Link>
);

const Badge = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className: string;
}) => (
  <span
    className={
      "text-xs border rounded-full px-2 lowercase font-mono bg-white flex items-center gap-1 " +
      className
    }
  >
    {children}
  </span>
);
